import type { MatrixService } from '@/app/matrix'

// Preset matrix targets per service (closed design decision 4). ARA has no
// deployed dev environment, so its only named presets are production and
// localhost; anything else goes through a Custom URL validated by the core
// allowlist (validateMatrixUrl).
export type MatrixPreset = {
  key: string
  url: string
}

export const CUSTOM_PRESET_KEY = 'custom'

export const MATRIX_PRESETS: Record<MatrixService, readonly MatrixPreset[]> = {
  ara: [
    { key: 'production', url: 'https://api.tractian.com/scope-ara' },
    { key: 'localhost', url: 'http://localhost:8002' }
  ],
  scoping: [
    { key: 'production', url: 'https://api.tractian.com/scoping' },
    { key: 'dev', url: 'https://api.int.tractian.dev/scoping' },
    { key: 'localhost', url: 'http://localhost:8888' }
  ]
}

/** Services in display/iteration order — derived from the presets map declaration. */
export const MATRIX_SERVICES = Object.keys(MATRIX_PRESETS) as readonly MatrixService[]

/**
 * One service row's edit state: a preset key, or CUSTOM_PRESET_KEY with the
 * free-text URL the user is typing.
 */
export type MatrixServiceSelection = {
  presetKey: string
  customUrl: string
}

/** Derive the form selection for a currently applied URL: matching preset, else Custom. */
export const selectionForUrl = (service: MatrixService, url: string): MatrixServiceSelection => {
  const preset = MATRIX_PRESETS[service].find(({ url: presetUrl }) => presetUrl === url)
  if (preset) return { presetKey: preset.key, customUrl: '' }
  return { presetKey: CUSTOM_PRESET_KEY, customUrl: url }
}

/** Resolve a selection back to the URL it stores — preset URL or the raw custom text. */
export const urlForSelection = (service: MatrixService, selection: MatrixServiceSelection) => {
  if (selection.presetKey === CUSTOM_PRESET_KEY) return selection.customUrl.trim()
  return MATRIX_PRESETS[service].find(({ key }) => key === selection.presetKey)?.url ?? ''
}
